/**
 * Grabación de consulta en cada episodio (dirección, 26/9/2026).
 *
 * «me gustaría añadir una opción de grabación de consulta que en cada episodio
 * tenga un botón que se ponga a grabar y así nos quede guardado el audio de esa
 * consulta por si se nos ha pasado algún detalle después. El audio será posible
 * escucharlo por uno mismo y administradores, enfermería también tiene la opción de
 * grabarlo igual que los doctores.»
 *
 *  · GRABAN médicos, enfermería y administradores. Se pulsa y graba: sin
 *    casilla de consentimiento, porque todos los pacientes están informados
 *    antes (dirección, 26/9/2026: «es una redundancia, se graba y punto»).
 *  · Mientras graba: aviso rojo que late con el tiempo («● Grabando 03:12»),
 *    Pausa / Seguir / Terminar, y aviso del navegador antes de salir. El audio
 *    sube a trozos mientras se graba (lib/grabadoraConsulta.js).
 *  · La grabación NO vive aquí sino en `gestorGrabacion` (segunda pasada,
 *    26/9/2026): cambiar de pestaña en la ficha, cerrar el episodio o ir a
 *    otra pantalla no la para. Mientras dura, la barra flotante de AppShell
 *    (components/BarraGrabacion.jsx) la enseña en todas las pantallas, y al
 *    volver a este episodio la tarjeta la enseña en curso.
 *  · ESCUCHAN quien la grabó y los administradores: el servidor solo le manda
 *    a cada uno las que puede oír. A los demás no se les enseña nada.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Pause, Play, Square, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { normalizeRole } from '../../lib/roles';
import {
  borrarGrabacion, formatoTiempo, gestorGrabacion, urlAudio, useGrabacionDelEpisodio,
} from '../../lib/grabadoraConsulta';

/** Quién ve el botón: lo mismo que el servidor (`ROLES_QUE_GRABAN`). */
export function puedeGrabarConsulta(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'doctor' || r === 'enfermeria';
}

export const EN_MARCHA = new Set(['pidiendo', 'grabando', 'pausada', 'guardando']);
const VELOCIDADES = [1, 1.5, 2];

function fechaHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** «2027-09-26» → «26/09/2027». */
function fechaCorta(dia) {
  const [a, m, d] = String(dia || '').slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : dia;
}

/** Una grabación de la lista, con su reproductor. El audio no se pide hasta
 *  pulsar «Escuchar»: cargarlo ya es escucharlo y queda en el rastro. */
function FilaGrabacion({ grabacion, onCambio }) {
  const [abierto, setAbierto] = useState(false);
  const [velocidad, setVelocidad] = useState(1);
  const [borrando, setBorrando] = useState(false);
  const audioRef = useRef(null);
  const g = grabacion;

  const cambiarVelocidad = (v) => {
    setVelocidad(v);
    if (audioRef.current) audioRef.current.playbackRate = v;
  };

  // El WebM que graba Chrome no dice cuánto dura: el reproductor enseña
  // «Infinity» y no deja saltar. Se le lleva al final un instante para que lo
  // mida y se vuelve al principio.
  const alCargar = (e) => {
    const a = e.currentTarget;
    a.playbackRate = velocidad;
    if (a.duration === Infinity || Number.isNaN(a.duration)) {
      const volver = () => {
        a.removeEventListener('timeupdate', volver);
        a.currentTime = 0;
        a.play?.()?.catch?.(() => {});
      };
      a.addEventListener('timeupdate', volver);
      try { a.currentTime = 1e101; } catch (err) { /* lo sabrá al terminar */ }
    }
  };

  const onBorrar = async () => {
    const cuando = fechaHora(g.inicio);
    if (!window.confirm(
      `¿Borrar la grabación de la consulta del ${cuando} (${formatoTiempo(g.duracion_s)})?\n\n`
      + 'Se borra el audio y no se puede recuperar. Queda apuntado quién la borró y cuándo.')) return;
    setBorrando(true);
    try {
      await borrarGrabacion(g.id);
      toast.success('Grabación borrada');
      onCambio?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar la grabación');
    } finally {
      setBorrando(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/30 p-2 space-y-2" data-testid="grabacion-fila">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Mic className="w-3.5 h-3.5 text-[#2E6E5E]" aria-hidden />
        <span className="font-medium text-[#13231E]">{fechaHora(g.inicio)}</span>
        <span className="text-muted-foreground">· {formatoTiempo(g.duracion_s)}</span>
        <span className="text-muted-foreground">· {g.grabado_por_nombre || '—'}</span>
        {g.estado === 'incompleta' && (
          <Badge variant="secondary" className="bg-amber-100 text-amber-900 border border-amber-300"
            title="Se cortó antes de terminar (pestaña cerrada o sin conexión): se oye lo que llegó.">
            Incompleta
          </Badge>
        )}
        {g.estado === 'grabando' && (
          <Badge variant="secondary" className="bg-red-100 text-red-800 border border-red-300">Grabándose</Badge>
        )}
        {/* Se guardan un año (dirección, 26/9/2026): la tarea diaria la borra. */}
        {g.se_borra_el && (
          <span className="text-xs text-muted-foreground" data-testid="grabacion-caduca">
            · Se borrará el {fechaCorta(g.se_borra_el)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {!abierto && (g.bytes > 0 || g.bytes === undefined) && (
            <Button size="sm" variant="outline" onClick={() => setAbierto(true)} data-testid="grabacion-escuchar">
              <Play className="w-3.5 h-3.5 mr-1" /> Escuchar
            </Button>
          )}
          {g.bytes === 0 && (
            <span className="text-xs text-muted-foreground" data-testid="grabacion-sin-audio">Aún sin audio</span>
          )}
          {g.puede_borrar && (
            <Button size="sm" variant="ghost" onClick={onBorrar} disabled={borrando}
              className="text-red-700 hover:text-red-800" data-testid="grabacion-borrar"
              aria-label="Borrar grabación">
              {borrando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          )}
        </div>
      </div>
      {abierto && (
        <div className="flex items-center gap-2 flex-wrap">
          <audio
            ref={audioRef}
            controls
            autoPlay
            preload="auto"
            src={urlAudio(g.id)}
            onLoadedMetadata={alCargar}
            className="h-9 w-full max-w-md"
            data-testid="grabacion-audio"
          />
          <div className="flex items-center gap-1" role="group" aria-label="Velocidad">
            {VELOCIDADES.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => cambiarVelocidad(v)}
                aria-pressed={velocidad === v}
                data-testid={`grabacion-velocidad-${String(v).replace('.', '_')}`}
                className={`px-2 py-0.5 rounded border text-xs ${velocidad === v
                  ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                  : 'border-border text-muted-foreground hover:text-[#13231E]'}`}
              >
                {String(v).replace('.', ',')}×
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GrabacionConsulta({
  episodio, paciente = null, user, grabaciones = [], onCambio,
}) {
  const puedeGrabar = puedeGrabarConsulta(user);
  const { estado, otra, ultima } = useGrabacionDelEpisodio(episodio.id);
  const caja = useRef(null);

  const fase = estado?.fase || 'reposo';
  const enMarcha = !!estado && EN_MARCHA.has(fase);

  // Se ha llegado desde la barra flotante: el episodio, a la vista.
  useEffect(() => {
    if (enMarcha && gestorGrabacion.tomarFoco(episodio.id)) {
      caja.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
  }, [enMarcha, episodio.id]);

  const onGrabar = async () => {
    if (enMarcha || otra) return;
    await gestorGrabacion.empezar({
      episodioId: episodio.id,
      patientId: paciente?.id || episodio.patient_id,
      pacienteNombre: paciente?.nombre || '',
    });
  };

  const error = (estado || ultima)?.error || '';
  const aviso = (estado || ultima)?.aviso || '';
  const oibles = (grabaciones || []).filter((g) => g.puede_escuchar);

  // A quien ni graba ni puede oír ninguna de este episodio, nada.
  if (!puedeGrabar && oibles.length === 0) return null;

  return (
    <div ref={caja} className="mt-3 pt-3 border-t border-border space-y-2" data-testid="grabacion-consulta">
      {puedeGrabar && !enMarcha && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={onGrabar}
            disabled={!!otra}
            title="Empezar a grabar la consulta"
            className="border-red-300 text-red-800 hover:bg-red-50"
            data-testid="grabacion-grabar"
          >
            <Mic className="w-3.5 h-3.5 mr-1.5" /> Grabar consulta
          </Button>
          {otra && (
            <span className="text-xs text-muted-foreground" data-testid="grabacion-otra">
              Hay otra grabación en marcha ({otra}): termínala antes de empezar esta.
            </span>
          )}
        </div>
      )}

      {enMarcha && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 flex items-center gap-3 flex-wrap"
          data-testid="grabacion-aviso"
        >
          <span className="font-semibold text-red-800 tabular-nums" data-testid="grabacion-tiempo">
            {fase === 'grabando' && (
              <>
                <span className="animate-pulse text-red-600" aria-hidden>● </span>
                Grabando {formatoTiempo(estado.segundos)}
              </>
            )}
            {fase === 'pausada' && <>❚❚ En pausa {formatoTiempo(estado.segundos)}</>}
            {fase === 'pidiendo' && 'Pidiendo el micrófono…'}
            {fase === 'guardando' && `Guardando la grabación (${formatoTiempo(estado.segundos)})…`}
          </span>
          {estado?.reintentando ? (
            <span className="text-xs text-red-800" data-testid="grabacion-reintentando">
              Sin conexión con el servidor: reintentando ({estado.intentos})… No cierres esta pestaña.
            </span>
          ) : (estado?.pendientes > 1 && (
            <span className="text-xs text-muted-foreground">Subiendo audio…</span>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {fase === 'grabando' && (
              <Button size="sm" variant="outline" onClick={() => gestorGrabacion.pausar()} data-testid="grabacion-pausa">
                <Pause className="w-3.5 h-3.5 mr-1" /> Pausa
              </Button>
            )}
            {fase === 'pausada' && (
              <Button size="sm" variant="outline" onClick={() => gestorGrabacion.seguir()} data-testid="grabacion-seguir">
                <Play className="w-3.5 h-3.5 mr-1" /> Seguir
              </Button>
            )}
            {(fase === 'grabando' || fase === 'pausada') && (
              <Button size="sm" onClick={() => gestorGrabacion.terminar()} className="bg-red-700 hover:bg-red-800 text-white"
                data-testid="grabacion-terminar">
                <Square className="w-3.5 h-3.5 mr-1" /> Terminar
              </Button>
            )}
            {fase === 'guardando' && <Loader2 className="w-4 h-4 animate-spin text-red-700" />}
          </div>
        </div>
      )}

      {aviso && (
        <div role="status" className="text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2"
          data-testid="grabacion-tope">
          {aviso}
        </div>
      )}
      {error && (
        <div role="alert" className="text-sm rounded-md border border-red-300 bg-red-50 text-red-800 px-3 py-2"
          data-testid="grabacion-error">
          {error}
        </div>
      )}

      {oibles.length > 0 && (
        <div className="space-y-2" data-testid="grabacion-lista">
          {oibles.map((g) => (
            <FilaGrabacion key={g.id} grabacion={g} onCambio={onCambio} />
          ))}
        </div>
      )}
    </div>
  );
}
